/**
 * Zod props schemas + admin preview samples of the Contrats usages that are
 * not covered by the shared `../shared/schemas.ts` (the list card and the
 * premiums table), plus the preview samples of the two info cards.
 *
 * Each schema is checked against its TypeScript props with `satisfies`.
 */

import { z } from 'zod';

import type { EntityInfoCardDataProps } from '../shared/schemas';
import type { ContractPremiumsTableCardProps } from './ContractPremiumsTableCard';
import type { ContractsListCardProps } from './ContractsListCard';

// ---------------------------------------------------------------------------
// extranet.contractsListCard
// ---------------------------------------------------------------------------

/** Rule-targetable props of the list card (`_layoutOverrides` is a meta prop). */
export type ContractsListCardDataProps = Pick<ContractsListCardProps, 'defaultStatus'>;

export const contractsListCardSchema = z.object({
  defaultStatus: z.string().optional(),
}) satisfies z.ZodType<ContractsListCardDataProps>;

export const CONTRACTS_LIST_PROP_LABELS = {
  defaultStatus: 'Statut sélectionné par défaut (EC, RESILIE, vide = tous)',
} as const;

export const CONTRACTS_LIST_PREVIEW_PROPS: ContractsListCardDataProps = {
  defaultStatus: 'EC',
};

// ---------------------------------------------------------------------------
// extranet.contractPremiumsCard
// ---------------------------------------------------------------------------

export type ContractPremiumsCardDataProps = Pick<
  ContractPremiumsTableCardProps,
  'rows' | 'loading' | 'error'
>;

export const contractPremiumRowSchema = z.object({
  id: z.string(),
  reference: z.string().nullable(),
  periodeDu: z.number().nullable(),
  periodeAu: z.number().nullable(),
  ttc: z.number().nullable(),
  situation: z.string().nullable(),
});

export const contractPremiumsCardDataSchema = z.object({
  rows: z.array(contractPremiumRowSchema),
  loading: z.boolean().optional(),
  error: z.string().nullable().optional(),
}) satisfies z.ZodType<ContractPremiumsCardDataProps>;

export const CONTRACT_PREMIUMS_PREVIEW_PROPS: ContractPremiumsCardDataProps = {
  rows: [
    {
      id: '21384',
      reference: 'RMSC102767',
      periodeDu: 1770332400000,
      periodeAu: 1798671600000,
      ttc: 1212.76,
      situation: 'Payée client',
    },
    {
      id: '21385',
      reference: 'RMSC102768',
      periodeDu: 1798758000000,
      periodeAu: 1830207600000,
      ttc: 1212.76,
      situation: 'Impayée',
    },
  ],
};

// ---------------------------------------------------------------------------
// Info cards — preview samples (fiches 22803 / 22805)
// ---------------------------------------------------------------------------

export const CONTRACT_DETAILS_PREVIEW_PROPS: EntityInfoCardDataProps = {
  values: {
    numero: 'Test CMI 123',
    produit: 'CONSTRUCTEUR DE MAISONS INDIVIDUELLES',
    assureur: 'ABEILLE',
    dateEffet: '07/09/2026',
    dateEcheance: '07/04/2028',
    primeAnnuelle: '1 449,83 €',
    fractionnement: 'Echéance Unique',
  },
};

export const CONTRACT_INSURED_PREVIEW_PROPS: EntityInfoCardDataProps = {
  values: {
    nom: 'A CIEL OUVERT GRAND SUD OUEST',
    siret: '812 456 789 00021',
    adresse: '5 RUE LE NOTRE, 44000 NANTES',
    email: 'contact@exemple.fr',
    telephone: '02 40 00 00 00',
  },
};
